import { useCallback, useEffect, useMemo, useState } from "react";
import { useUrlState } from "@/lib/useUrlState";
import {
  Upload, Plus, Layers, Pencil, ChevronLeft, ChevronRight, Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SearchBar from "@/components/SearchBar";
import Segmented from "@/components/Segmented";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import FilterPanel from "./components/FilterPanel";
import ItemGrid from "./components/ItemGrid";
import GroupsGrid from "./components/GroupsGrid";
import ColumnPicker from "./components/ColumnPicker";
import ItemDrawer from "./components/ItemDrawer";
import GroupDrawer from "./components/GroupDrawer";
import BulkEditDialog from "./components/BulkEditDialog";
import PriceHistoryDialog from "./components/PriceHistoryDialog";
import AddToGroupDialog from "./components/AddToGroupDialog";
import PriceUpdateDialog from "./components/PriceUpdateDialog";
import { COLUMNS, DEFAULT_VISIBLE, STORAGE_KEY } from "./lib/columns";
import { estApi } from "./lib/api";
import { useAuth } from "@/lib/AuthContext";
import LaborLibraryPanel from "./components/LaborLibraryPanel";

const EMPTY_FILTERS = {
  search: "", flag: "", category: "", manufacturer: "", cost_code_id: "", active: "",
};
const PAGE_SIZE = 100;

export default function PriceListPage() {
  const { principal, isAdmin } = useAuth();
  // Locked: only Estimators and Admins edit the Cost Library (server enforces the same).
  const canEdit = isAdmin || principal?.tabs?.["estimating.cost_library"] === "write";
  const [facets, setFacets] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [view, setView] = useUrlState("view", "items");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState("name");
  const [direction, setDirection] = useState("asc");
  const [selected, setSelected] = useState([]);

  const [groups, setGroups] = useState([]);
  const [options, setOptions] = useState(null);

  const [itemDrawer, setItemDrawer] = useState(null);      // item object or {} for new
  const [groupDrawer, setGroupDrawer] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [historyItem, setHistoryItem] = useState(null);
  const [addToGroupIds, setAddToGroupIds] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);   // { kind: 'item'|'group', row }
  const [importOpen, setImportOpen] = useState(false);

  const [visible, setVisible] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      return Array.isArray(saved) && saved.length ? saved : DEFAULT_VISIBLE;
    } catch { return DEFAULT_VISIBLE; }
  });
  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(visible)); }, [visible]);

  const query = useMemo(() => ({
    ...filters,
    active: filters.active === "" ? undefined : filters.active === "true",
    sort, direction, skip: page * PAGE_SIZE, limit: PAGE_SIZE,
  }), [filters, sort, direction, page]);

  // Same coercion as `query`, minus paging — an empty `active` would 422 the export.
  const exportParams = useMemo(() => ({
    ...filters,
    active: filters.active === "" ? undefined : filters.active === "true",
  }), [filters]);

  const templateParams = useMemo(() => ({
    search: filters.search, category: filters.category, manufacturer: filters.manufacturer,
    active_only: filters.active !== "false",
  }), [filters]);

  const loadFacets = useCallback(() => { estApi.getFacets().then(setFacets).catch(() => {}); }, []);
  const loadGroups = useCallback(() => {
    estApi.getGroups({ active: true }).then((r) => setGroups(r.groups)).catch(() => {});
  }, []);
  const loadItems = useCallback(() => {
    estApi.getPriceList(query).then((r) => { setItems(r.items); setTotal(r.total); }).catch(() => {});
  }, [query]);

  useEffect(() => { loadFacets(); loadGroups(); estApi.getCodeOptions().then(setOptions).catch(() => {}); },
    [loadFacets, loadGroups]);
  useEffect(() => {
    const t = setTimeout(loadItems, 200);
    return () => clearTimeout(t);
  }, [loadItems]);

  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(0); setSelected([]); };
  const resetFilters = () => { setFilters(EMPTY_FILTERS); setPage(0); };

  const onSort = (key) => {
    if (sort === key) setDirection((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(key); setDirection("asc"); }
    setPage(0);
  };

  const inlineEdit = async (item, patch) => {
    if (patch.__open) return setItemDrawer(item);
    try {
      await estApi.updateItem(item.id, patch);
      toast.success("Saved");
      loadItems();
      loadFacets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const duplicate = async (item) => {
    await estApi.duplicateItem(item.id);
    toast.success("Item duplicated");
    loadItems();
  };

  const confirmToggleActive = async () => {
    const it = deactivateTarget;
    setDeactivateTarget(null);
    await estApi.setItemActive(it.id, !it.active);
    toast.success(it.active ? "Item deactivated" : "Item reactivated");
    loadItems();
    loadFacets();
  };

  const confirmDelete = async () => {
    const { kind, row } = deleteTarget;
    setDeleteTarget(null);
    try {
      if (kind === "item") {
        await estApi.deleteItem(row.id);
        toast.success("Item deleted");
        loadItems();
        loadFacets();
      } else {
        await estApi.deleteGroup(row.id);
        toast.success("Group deleted");
        loadGroups();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const pages = Math.ceil(total / PAGE_SIZE) || 1;
  const showItems = view === "items" || view === "both";
  const showGroups = view === "groups" || view === "both";

  return (
    <div className="page-pane gap-3" data-testid="price-list-page">
      {/* Bid-Board-style header: its own row above the grid card */}
      <div className="flex shrink-0 flex-wrap items-center gap-2" data-testid="price-list-toolbar">
        <SearchBar value={filters.search} onChange={(v) => setFilter("search", v)} placeholder="Search..." testId="filter-search" />
        <Segmented
          options={["items", "groups", "labor"].map((v) => ({
            key: v, label: v.charAt(0).toUpperCase() + v.slice(1), testId: `view-toggle-${v}` }))}
          value={view}
          onChange={setView}
        >
          <span className="mx-0.5 h-4 w-px bg-slate-300" />
          <ColumnPicker columns={COLUMNS} visible={visible}
                        onToggle={(k) => setVisible((v) => v.includes(k) ? v.filter((x) => x !== k) : [...v, k])}
                        onReset={() => setVisible(DEFAULT_VISIBLE)} />
        </Segmented>
        {!canEdit && (
          <span className="inline-flex items-center gap-1 rounded-sm border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-500"
                title="Only Estimators and Admins can edit the Cost Library" data-testid="cost-library-locked">
            <Lock className="h-3.5 w-3.5" /> View only
          </span>
        )}
        {canEdit && (<>
          <Button size="sm" className="h-7 px-2 text-xs" onClick={() => setItemDrawer({})} data-testid="add-item-btn">
            <Plus className="mr-0.5 h-3.5 w-3.5" /> Item
          </Button>
          <Button size="sm" className="h-7 px-2 text-xs" onClick={() => setGroupDrawer({})} data-testid="add-group-btn">
            <Plus className="mr-0.5 h-3.5 w-3.5" /> Group
          </Button>
        </>)}
        {canEdit && <Button variant="data" size="sm" className="h-7 px-2 text-xs" onClick={() => setImportOpen(true)} title="Upload pricing from a spreadsheet · export CSV"
                data-testid="open-price-update-btn">
          <Upload className="mr-1 h-3.5 w-3.5" /> Pricing
        </Button>}
      </div>

      <div className="pane-card border border-slate-200 bg-white">
      <div className="pane-row flex flex-1 overflow-hidden">
      <FilterPanel facets={facets} filters={filters} setFilter={setFilter} resetFilters={resetFilters} />

      <div className="flex-1 flex flex-col overflow-hidden bg-white">
        {(showGroups || (canEdit && selected.length > 0)) && (
        <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-1.5">
          {showGroups && <span className="text-xs text-slate-500" data-testid="price-list-count">{groups.length} group{groups.length === 1 ? "" : "s"}</span>}
          {canEdit && selected.length > 0 && (
            <>
              <span className="text-xs font-medium text-[#007BFF]" data-testid="selection-count">
                {selected.length} selected
              </span>
              <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-edit-btn">
                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Bulk Edit
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAddToGroupIds(selected)}
                      data-testid="bulk-add-to-group-btn">
                <Layers className="h-3.5 w-3.5 mr-1.5" /> Add to Group
              </Button>
              <button onClick={() => setSelected([])} className="text-xs text-slate-400 hover:text-slate-700"
                      data-testid="clear-selection-btn">clear</button>
            </>
          )}
        </div>)}

        <div className="flex-1 flex flex-col gap-2 overflow-hidden">
          {view === "labor" && <LaborLibraryPanel />}
          {showGroups && (
            <div className={showItems ? "max-h-[38%] shrink-0 overflow-hidden flex flex-col" : "flex-1 overflow-hidden"}>
              <GroupsGrid
                groups={groups}
                onEdit={(g) => estApi.getGroup(g.id).then(setGroupDrawer)}
                onToggleActive={async (g) => {
                  await estApi.setGroupActive(g.id, !g.active);
                  toast.success(g.active ? "Group deactivated" : "Group reactivated");
                  loadGroups();
                }}
                onDelete={(g) => setDeleteTarget({ kind: "group", row: g })}
                readOnly={!canEdit}
              />
            </div>
          )}
          {showItems && (
            <ItemGrid
              items={items} visible={visible} sort={sort} direction={direction} onSort={onSort}
              selected={selected}
              toggleSelect={(id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id])}
              toggleSelectAll={() => setSelected((s) =>
                items.every((i) => s.includes(i.id)) ? [] : items.map((i) => i.id))}
              onEdit={setItemDrawer}
              onDuplicate={duplicate}
              onToggleActive={setDeactivateTarget}
              onHistory={setHistoryItem}
              onAddToGroup={(it) => setAddToGroupIds([it.id])}
              costCodes={options?.cost_codes || []}
              onDelete={(it) => setDeleteTarget({ kind: "item", row: it })}
              onInlineEdit={inlineEdit}
              readOnly={!canEdit}
            />
          )}
        </div>
        {showItems && (
          <div className="flex items-center justify-end gap-1 border-t border-slate-200 px-4 py-1.5" data-testid="price-list-pager">
            <span className="mr-2 text-xs text-slate-500">{total.toLocaleString()} items</span>
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}
                    className="p-1 rounded text-slate-500 disabled:opacity-30 hover:bg-slate-100"
                    data-testid="page-prev"><ChevronLeft className="h-4 w-4" /></button>
            <span className="text-xs text-slate-500" data-testid="page-indicator">
              Page {page + 1} of {pages}
            </span>
            <button disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}
                    className="p-1 rounded text-slate-500 disabled:opacity-30 hover:bg-slate-100"
                    data-testid="page-next"><ChevronRight className="h-4 w-4" /></button>
          </div>)}
      </div>
      </div>

      <ItemDrawer open={!!itemDrawer} onOpenChange={(o) => !o && setItemDrawer(null)}
                  item={itemDrawer} options={options}
                  onSaved={() => { loadItems(); loadFacets(); }} />

      <GroupDrawer open={!!groupDrawer} onOpenChange={(o) => !o && setGroupDrawer(null)}
                   group={groupDrawer} options={options} groups={groups}
                   onSaved={loadGroups} />

      {bulkOpen && (
        <BulkEditDialog open onOpenChange={setBulkOpen} ids={selected} options={options}
                        onDone={() => { setSelected([]); loadItems(); loadFacets(); }} />
      )}

      <PriceHistoryDialog open={!!historyItem} onOpenChange={(o) => !o && setHistoryItem(null)}
                          item={historyItem} />

      {addToGroupIds && (
        <AddToGroupDialog open onOpenChange={(o) => !o && setAddToGroupIds(null)}
                          itemIds={addToGroupIds} groups={groups}
                          onCreateGroup={() => { setAddToGroupIds(null); setGroupDrawer({}); }}
                          onDone={() => { setAddToGroupIds(null); loadGroups(); }} />
      )}

      <AlertDialog open={!!deactivateTarget} onOpenChange={(o) => !o && setDeactivateTarget(null)}>
        <AlertDialogContent data-testid="deactivate-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deactivateTarget?.active ? "Deactivate" : "Reactivate"} "{deactivateTarget?.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deactivateTarget?.active
                ? "Inactive items stay in the catalog but won't appear in estimate searches."
                : "This item will appear in estimate searches again."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deactivate-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmToggleActive} data-testid="deactivate-confirm">
              Yes, {deactivateTarget?.active ? "deactivate" : "reactivate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.row?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.kind === "item"
                ? "This permanently removes the item and its price history. Only manually created items can be deleted — Procore-imported rows are deactivated instead."
                : "This permanently removes the group and detaches it from any parent group. Estimates already built from it keep their lines."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="delete-confirm">Yes, delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PriceUpdateDialog open={importOpen} onOpenChange={setImportOpen}
                         templateParams={templateParams}
                         onExportCsv={() => estApi.exportCsv(exportParams).then(() => toast.success("CSV downloaded")).catch(() => toast.error("Export failed"))}
                         onCommitted={() => { loadItems(); loadFacets(); }} />

      </div>
    </div>
  );
}
